import {
  useEffect, useState, useMemo, memo
} from 'react';
import PropTypes from 'prop-types';
import {
  Box,
  Card,
  Chip,
  Table,
  TableBody,
  TableCell,
  TableRow,
  Typography,
  useTheme,
  useMediaQuery,
  IconButton,
  Tooltip,
  Stack,
  Divider
} from '@mui/material';
import { ChevronRight as ChevronRightIcon } from '@mui/icons-material';
import { useHistory } from 'react-router-dom';
import ROUTE from 'src/utils/routeConstants';
import { useDispatch, useSelector } from 'react-redux';
import { getTyreList } from 'src/redux/tyreSlice';
import Loader from 'src/common/Loader';
import EnhancedTableHead, { getComparator } from 'src/common/EnhancedTableHead';
import {
  getAlignByColumnId,
  tyreDataTableHeaders
} from 'src/utils/tableHeaders';
import CustomPagination from 'src/common/CustomPagination';
import { getSelectedSiteFilterValue } from 'src/utils/getInitials';
import {
  getTableActionIconButtonSx,
  TABLE_ACTION_ICON_SX
} from 'src/common/tableActionStyles';

// --------------------
// Mobile Card View
// --------------------

const MobileCardView = memo(({ tyre, onViewClick, theme }) => (
  <Card
    sx={{
      mb: 2,
      p: 2,
      borderRadius: 2,
      border: '1px solid',
      borderColor: 'divider',
      boxShadow: 'none'
    }}
  >
    <Stack direction="row" justifyContent="space-between" mb={2}>
      <Box>
        <Typography variant="subtitle1" fontWeight={700}>
          {tyre.tyre_number}
        </Typography>
        <Typography variant="caption" color="text.secondary">
          #
          {tyre.id}
        </Typography>
      </Box>

      <Chip
        label={!tyre.expired ? 'Active' : 'Inactive'}
        color={!tyre.expired ? 'success' : 'error'}
        variant="outlined"
        size="small"
        sx={{ fontWeight: 600 }}
      />
    </Stack>

    <Divider sx={{ mb: 2 }} />

    <Typography variant="body2" color="text.secondary">
      Vehicle:
      {tyre.vehicle_number || '-'}
    </Typography>

    <Typography variant="body2" color="text.secondary">
      Km Travelled:
      {tyre.km_travelled || 0}
    </Typography>

    <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
      Figment Date:
      {tyre.figment_date || '-'}
    </Typography>

    <Box textAlign="right">
      <IconButton
        size="medium"
        aria-label="View details"
        sx={getTableActionIconButtonSx(theme)}
        onClick={() => onViewClick(tyre.id)}
      >
        <ChevronRightIcon sx={TABLE_ACTION_ICON_SX} />
      </IconButton>
    </Box>
  </Card>
));

MobileCardView.propTypes = {
  tyre: PropTypes.object.isRequired,
  onViewClick: PropTypes.func.isRequired,
  theme: PropTypes.object.isRequired
};

// --------------------
// Main Component
// --------------------

const TyreList = ({ refreshData, searchText, siteFilter }) => {
  const theme = useTheme();
  const history = useHistory();
  const dispatch = useDispatch();

  const {
    tyreList = [],
    totalCount = 0,
    loading
  } = useSelector((state) => state.tyres);

  const isMobile = useMediaQuery(theme.breakpoints.down('md'));

  const [limit, setLimit] = useState(10);
  const [page, setPage] = useState(0);
  const [order, setOrder] = useState('desc');
  const [orderBy, setOrderBy] = useState('id');
  const tyreAlignById = useMemo(
    () => getAlignByColumnId(tyreDataTableHeaders),
    []
  );
  const selectedSite = getSelectedSiteFilterValue(siteFilter);

  useEffect(() => {
    setPage(0);
    dispatch(
      getTyreList({
        limit,
        pageNo: 1,
        params: { tyre_number: searchText, site: selectedSite }
      })
    );
  }, [dispatch, limit, refreshData, searchText, selectedSite]);

  const handleLimitChange = (event) => {
    const newLimit = parseInt(event.target.value, 10);
    setLimit(newLimit);
    setPage(0);
  };

  const handlePageChange = (event, newPage) => {
    setPage(newPage);
    dispatch(
      getTyreList({
        limit,
        pageNo: newPage + 1,
        params: { tyre_number: searchText, site: selectedSite }
      })
    );
  };

  const viewTyreInfo = (id) => {
    history.push(`${ROUTE.tyreManangement}/${id}`);
  };

  const handleRequestSort = (e, property) => {
    const isAsc = orderBy === property && order === 'asc';
    setOrder(isAsc ? 'desc' : 'asc');
    setOrderBy(property);
  };

  const displayData = tyreList?.slice().sort(getComparator(order, orderBy)) || [];

  if (loading) return <Loader />;

  return (
    <>
      {isMobile ? (
        <Box>
          {displayData.length > 0 ? (
            displayData.map((tyre) => (
              <MobileCardView
                key={tyre.id}
                tyre={tyre}
                theme={theme}
                onViewClick={viewTyreInfo}
              />
            ))
          ) : (
            <Card sx={{ p: 3, textAlign: 'center' }}>
              <Typography color="text.secondary">No tyres found</Typography>
            </Card>
          )}

          <CustomPagination
            page={page}
            rowsPerPage={limit}
            count={totalCount}
            onPageChange={handlePageChange}
            onRowsPerPageChange={handleLimitChange}
          />
        </Box>
      ) : (
        <Card
          sx={{
            borderRadius: 2,
            boxShadow: '0 1px 3px rgba(0,0,0,0.1)',
            height: '100%',
            display: 'flex',
            flexDirection: 'column',
            overflow: 'hidden'
          }}
        >
          <Box
            sx={{
              minHeight: 0,
              flex: 1,
              overflow: 'auto',
              '& .MuiTableCell-stickyHeader': {
                backgroundColor: '#F9FAFB',
                borderBottom: `1px solid ${theme.palette.divider}`,
                zIndex: 2
              },
              '& .MuiTableHead-root .MuiTableCell-root:last-of-type': {
                position: 'sticky',
                right: 0,
                zIndex: 3,
                backgroundColor: '#F9FAFB',
                boxShadow: '-1px 0 0 rgba(0, 0, 0, 0.08)'
              }
            }}
          >
            <Box sx={{ minWidth: { xs: 740, sm: 880, md: 1000 } }}>
              <Table stickyHeader>
                <EnhancedTableHead
                  order={order}
                  orderBy={orderBy}
                  headCells={tyreDataTableHeaders}
                  onRequestSort={handleRequestSort}
                />
                <TableBody>
                  {displayData.length > 0 ? (
                    displayData.map((tyre) => (
                      <TableRow
                        key={tyre.id}
                        hover
                        sx={{
                          '&:hover': {
                            backgroundColor: '#F9FAFB'
                          }
                        }}
                      >
                        <TableCell align={tyreAlignById.tyre_number}>
                          {tyre.tyre_number}
                        </TableCell>
                        <TableCell align={tyreAlignById.km_travelled}>
                          {tyre.km_travelled}
                        </TableCell>
                        <TableCell align={tyreAlignById.vehicle_number}>
                          {tyre.vehicle_number}
                        </TableCell>
                        <TableCell align={tyreAlignById.figment_date}>
                          {tyre.figment_date}
                        </TableCell>

                        <TableCell align={tyreAlignById.expired}>
                          <Chip
                            label={!tyre.expired ? 'Active' : 'Inactive'}
                            color={!tyre.expired ? 'success' : 'error'}
                            size="small"
                            variant="outlined"
                            sx={{ fontWeight: 600 }}
                          />
                        </TableCell>

                        <TableCell
                          align={tyreAlignById.action}
                          sx={{
                            position: 'sticky',
                            right: 0,
                            zIndex: 1,
                            backgroundColor: '#FFFFFF',
                            boxShadow: '-1px 0 0 rgba(0, 0, 0, 0.08)'
                          }}
                        >
                          <Tooltip title="View Details">
                            <IconButton
                              size="medium"
                              aria-label="View details"
                              sx={getTableActionIconButtonSx(theme)}
                              onClick={(e) => {
                                e.stopPropagation();
                                viewTyreInfo(tyre.id);
                              }}
                            >
                              <ChevronRightIcon sx={TABLE_ACTION_ICON_SX} />
                            </IconButton>
                          </Tooltip>
                        </TableCell>
                      </TableRow>
                    ))
                  ) : (
                    <TableRow>
                      <TableCell
                        colSpan={tyreDataTableHeaders.length}
                        align="center"
                      >
                        <Typography color="text.secondary">
                          No tyres found
                        </Typography>
                      </TableCell>
                    </TableRow>
                  )}
                </TableBody>
              </Table>
            </Box>
          </Box>

          <Divider sx={{ flexShrink: 0 }} />
          <Box sx={{ flexShrink: 0, bgcolor: '#FFFFFF' }}>
            <CustomPagination
              page={page}
              rowsPerPage={limit}
              count={totalCount}
              onPageChange={handlePageChange}
              onRowsPerPageChange={handleLimitChange}
            />
          </Box>
        </Card>
      )}
    </>
  );
};

TyreList.propTypes = {
  refreshData: PropTypes.bool,
  searchText: PropTypes.string,
  siteFilter: PropTypes.oneOfType([PropTypes.string, PropTypes.number])
};

export default TyreList;
